import { UseQueryOptions, useQuery } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import { SortOrder } from '@/components/ui/table'
import { ClientModel } from '@/models/features/clients'
import {
  ClientFilterStatus,
  PMSConnectedBuildingStatusEnum,
} from '@/pages/clients/clients-table/clients-table.types'
import { keepPreviousDataWithPlaceholder } from '@/utils/api'

export interface GetClientsDashboardRequest {
  skip?: number
  limit?: number
  buildingId?: number
  search?: string
  status?: Exclude<ClientFilterStatus, typeof PMSConnectedBuildingStatusEnum.enum.all>
  sortBy?: string
  sortOrder?: SortOrder
  appUsers?: boolean
}

export interface GetClientsDashboardResponse {
  count: number
  clients: ClientModel[]
  currentCount: number
  activeCount: number
  pendingCount: number
  archivedCount: number
  futureCount: number
  noticeCount: number
  pastCount: number
}

export const getClientsDashboard = async ({
  data: { appUsers, skip, limit, buildingId, search, status, sortBy, sortOrder },
  signal,
}: {
  data: GetClientsDashboardRequest
  signal?: AbortSignal
}) => {
  const response = await authAPI.get<GetClientsDashboardResponse>(`/client/dashboard`, {
    params: { appUsers, buildingId, limit, search, skip, sortBy, sortOrder, status },
    signal,
  })

  return response.data
}

export const BASE_CLIENT_DASHBOARD_QUERY_KEY = 'useGetClientsDashboard'
export const getClientsDashboardQueryKey = (data: GetClientsDashboardRequest) =>
  [BASE_CLIENT_DASHBOARD_QUERY_KEY, data] as const

const placeholderData: GetClientsDashboardResponse = {
  activeCount: 0,
  archivedCount: 0,
  clients: [],
  count: 0,
  currentCount: 0,
  futureCount: 0,
  noticeCount: 0,
  pastCount: 0,
  pendingCount: 0,
}

export const useGetClientsDashboard = (
  data?: GetClientsDashboardRequest,
  options?: Omit<
    UseQueryOptions<
      GetClientsDashboardResponse,
      Error,
      GetClientsDashboardResponse,
      ReturnType<typeof getClientsDashboardQueryKey>
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const enabled =
    data !== undefined &&
    data.skip !== undefined &&
    data.skip >= 0 &&
    !!data.buildingId &&
    !!data.limit

  return useQuery<
    GetClientsDashboardResponse,
    Error,
    GetClientsDashboardResponse,
    ReturnType<typeof getClientsDashboardQueryKey>
  >({
    enabled,
    placeholderData: keepPreviousDataWithPlaceholder(placeholderData),
    queryFn: ({ signal }) => getClientsDashboard({ data: data ?? {}, signal }),
    queryKey: getClientsDashboardQueryKey(data ?? {}),
    ...options,
  })
}
